import { loadGoogleFont } from './loadGoogleFont';

export const SHADE_LEVELS = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'] as const;
export type ShadeLevel = (typeof SHADE_LEVELS)[number];
export type ShadeOverrides = Partial<Record<ShadeLevel, string>>;

// Lightness of each shade when the base color sits at the 600 step's standard lightness.
// Keep in sync with the first-paint script in index.html.
const STANDARD_LIGHTNESS: Record<ShadeLevel, number> = {
  '50': 97, '100': 94, '200': 86, '300': 76, '400': 60, '500': 45,
  '600': 38, '700': 32, '800': 26, '900': 22, '950': 16,
};
const STANDARD_600_LIGHTNESS = 38;

export const BUTTON_RADIUS_PILL = 9999;
export const DEFAULT_BUTTON_RADIUS = 12;

export interface SiteStylesData {
  h1_font?: string;
  body_font?: string;
  primary_color?: string;
  secondary_color?: string;
  primary_shades?: ShadeOverrides | null;
  secondary_shades?: ShadeOverrides | null;
  primary_btn_bg?: string | null;
  primary_btn_hover?: string | null;
  primary_btn_font_color?: string;
  secondary_btn_bg?: string | null;
  secondary_btn_hover?: string | null;
  secondary_btn_font_color?: string;
  button_radius?: number | null;
  card_radius?: number;
  decoration_opacity?: number;
}

export function isValidHex(color: string) {
  return /^#([0-9A-Fa-f]{3}){1,2}$/.test(color);
}

function hexToHsl(hex: string): { h: number; s: number; l: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return null;

  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function hslToHex(h: number, s: number, l: number): string {
  const sat = s / 100;
  const light = l / 100;
  const a = sat * Math.min(light, 1 - light);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = light - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`.toUpperCase();
}

/**
 * The 50–950 scale derived from a base color, as hex. The base color lands on 600 and the
 * other steps keep their standard distance from it.
 */
export function deriveShades(baseColor: string): Record<ShadeLevel, string> | null {
  const hsl = hexToHsl(baseColor);
  if (!hsl) return null;

  const offset = hsl.l - STANDARD_600_LIGHTNESS;
  const shades = {} as Record<ShadeLevel, string>;
  SHADE_LEVELS.forEach((level) => {
    const lightness = Math.min(98, Math.max(5, STANDARD_LIGHTNESS[level] + offset));
    shades[level] = hslToHex(hsl.h, hsl.s, lightness);
  });
  return shades;
}

/** Derived shades with any admin overrides on top. */
export function resolveShades(baseColor: string | undefined, overrides?: ShadeOverrides | null) {
  const derived = baseColor ? deriveShades(baseColor) : null;
  if (!derived) return null;
  const resolved = { ...derived };
  Object.entries(overrides || {}).forEach(([level, hex]) => {
    if (hex && isValidHex(hex) && level in resolved) resolved[level as ShadeLevel] = hex;
  });
  return resolved;
}

/**
 * CSS variables for the given styles. Button colors left blank come back as null (remove
 * the variable) so the `.site-btn-*` rules in index.css fall back to the brand shades.
 */
export function buildStyleVars(data: SiteStylesData): Record<string, string | null> {
  const vars: Record<string, string | null> = {};

  if (data.h1_font) vars['--font-heading'] = data.h1_font;
  if (data.body_font) vars['--font-body'] = data.body_font;

  (['primary', 'secondary'] as const).forEach((palette) => {
    const base = palette === 'primary' ? data.primary_color : data.secondary_color;
    const overrides = palette === 'primary' ? data.primary_shades : data.secondary_shades;
    const shades = resolveShades(base, overrides);
    if (shades) {
      SHADE_LEVELS.forEach((level) => {
        vars[`--color-${palette}-${level}`] = shades[level];
      });
    }
  });

  const optionalColor = (value: string | null | undefined) => (value && isValidHex(value) ? value : null);
  if ('primary_btn_bg' in data) vars['--btn-primary-bg'] = optionalColor(data.primary_btn_bg);
  if ('primary_btn_hover' in data) vars['--btn-primary-bg-hover'] = optionalColor(data.primary_btn_hover);
  if ('secondary_btn_bg' in data) vars['--btn-secondary-bg'] = optionalColor(data.secondary_btn_bg);
  if ('secondary_btn_hover' in data) vars['--btn-secondary-bg-hover'] = optionalColor(data.secondary_btn_hover);
  if (data.primary_btn_font_color) vars['--btn-primary-text'] = data.primary_btn_font_color;
  if (data.secondary_btn_font_color) vars['--btn-secondary-text'] = data.secondary_btn_font_color;
  if (data.button_radius != null) vars['--btn-radius'] = `${data.button_radius}px`;
  if (data.card_radius != null) vars['--card-radius'] = `${data.card_radius}px`;

  return vars;
}

/** Applies site styles to the document. Keys missing from `data` are left untouched. */
export function applySiteStyles(data: SiteStylesData) {
  const root = document.documentElement;
  Object.entries(buildStyleVars(data)).forEach(([name, value]) => {
    if (value == null) root.style.removeProperty(name);
    else root.style.setProperty(name, value);
  });
  if (data.h1_font) loadGoogleFont(data.h1_font);
  if (data.body_font) loadGoogleFont(data.body_font);
}
